import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { HeroBannerSliceData, PageType } from '@adapters/prismic/types/slices';
import Container from '@components/global/Container';
import Button from '@components/ui/Button';
import Heading from '@components/ui/Heading';
import { eePromoImpHandler, setPromoDetailsCookie } from '@utils/gtm';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { useLocale } from '@hooks/useLocale';
import Image from 'next/image';
import MarkdownCopy from '../MarkdownCopy';
import { formatHeroText } from './FormatHeroText/index';

import styles from './HeroBanner.module.scss';

interface HeroBannerProps {
  mediaType: HeroBannerSliceData['media_type'];
  mobileImage: HeroBannerSliceData['mobile_image'];
  desktopImage: HeroBannerSliceData['desktop_image'];
  mobileVideo: HeroBannerSliceData['mobile_video'];
  desktopVideo: HeroBannerSliceData['desktop_video'];
  videoLabel: HeroBannerSliceData['video_alt_text'];
  url: HeroBannerSliceData['url'];
  blackText: HeroBannerSliceData['black_text'];
  redText: HeroBannerSliceData['red_text'];
  redTextPlacement: HeroBannerSliceData['red_text_placement'];
  redTextNewLine: HeroBannerSliceData['red_text_new_line'];
  bodyCopy: HeroBannerSliceData['body_copy'];
  ctaText: HeroBannerSliceData['cta_text'];
  ctaType: HeroBannerSliceData['cta_type'];
  trackPromos: HeroBannerSliceData['track_promos'];
  pageType: PageType;
  sliceIndex: number;
}

const HeroBanner = ({
  mediaType,
  mobileImage,
  desktopImage,
  mobileVideo,
  desktopVideo,
  videoLabel,
  url,
  blackText,
  redText,
  redTextPlacement,
  redTextNewLine,
  bodyCopy,
  ctaText,
  ctaType,
  trackPromos,
  pageType,
  sliceIndex,
}: HeroBannerProps): ReactElement => {
  const locale = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useObjectVisibility(ref);

  const promoDetails = useMemo(
    () => ({
      id: ctaText,
      name: ctaText,
      type: 'link',
      url: url,
      position: `${pageType}-${sliceIndex}-HeroBanner`,
      isVideo: mediaType === 'video' ? true : false,
    }),
    [ctaText, url, pageType, sliceIndex, mediaType]
  );

  useEffect(() => {
    if (trackPromos && isVisible) {
      eePromoImpHandler(promoDetails);
    }
  }, [promoDetails, isVisible, trackPromos]);

  const handleClick = () => {
    setPromoDetailsCookie(promoDetails);
  };

  const desktopImg = (
    <Image
      src={desktopImage?.url}
      alt={desktopImage?.alt}
      width={desktopImage?.dimensions?.width}
      height={desktopImage?.dimensions?.height}
      priority={sliceIndex === 0 ? true : false}
      sizes="100vw"
      style={{
        width: '100%',
        height: 'auto',
      }}
    />
  );

  const mobileImg = (
    <Image
      src={mobileImage?.url}
      alt={mobileImage?.alt}
      width={mobileImage?.dimensions?.width}
      height={mobileImage?.dimensions?.height}
      priority={sliceIndex === 0 ? true : false}
      sizes="100vw"
      style={{
        width: '100%',
        height: 'auto',
      }}
    />
  );

  const images = desktopImage && mobileImage && (
    <>
      {desktopImage && <div className={styles.desktopImage}>{desktopImg}</div>}
      {mobileImage && <div className={styles.mobileImage}>{mobileImg}</div>}
    </>
  );

  const commonVideoProps = {
    autoPlay: true,
    muted: true,
    loop: true,
    playsInline: true,
    controlsList: 'nofullscreen, nodownload',
    disablePictureInPicture: true,
    preload: 'auto',
    'aria-label': videoLabel,
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
  };

  const videos = desktopVideo && mobileVideo && (
    <>
      {desktopVideo && (
        <div className={styles.desktopImage}>
          <video {...commonVideoProps} poster={desktopImage?.url}>
            <source src={desktopVideo} type="video/mp4" />
            {/* Fallback for when video cannot play */}
            {desktopImg}
          </video>
        </div>
      )}
      {mobileVideo && (
        <div className={styles.mobileImage}>
          <video {...commonVideoProps} poster={mobileImage?.url}>
            <source src={mobileVideo} type="video/mp4" />
            {/* Fallback for when video cannot play */}
            {mobileImg}
          </video>
        </div>
      )}
    </>
  );

  const cta = url && ctaText && (
    <Button
      label={ctaText}
      size="large"
      variant={ctaType}
      type="button"
      href={`/${locale}${url}`}
      handleClick={trackPromos ? () => handleClick() : undefined}
    />
  );

  return (
    <div ref={ref} className={styles.heroBanner}>
      <div className={styles.imageContainer}>
        <a
          href={`/${locale}${url}`}
          onClick={trackPromos ? handleClick : undefined}
        >
          {mediaType === 'image' ? images : videos}
        </a>
        <div className={styles.mobileCta}>{cta}</div>
      </div>
      <Container className={styles.contentContainer}>
        <Heading semanticLevel="h1" styleLevel="1" className={styles.heading}>
          {formatHeroText(blackText, redText, redTextPlacement, redTextNewLine)}
        </Heading>
        <div className={styles.bodyCopyContainer}>
          <MarkdownCopy
            text={bodyCopy?.[0]?.text ?? ''}
            className={styles.bodyCopy}
          />
        </div>
        <div className={styles.desktopCta}>{cta}</div>
      </Container>
    </div>
  );
};

export default HeroBanner;
